/**
 * A client's outside-counsel guidelines on file: upload the PDF or Word file,
 * CaseBlink reads the billing rules out of it with the section each comes
 * from, and the admin reviews them. Pre-bill checks released time on the
 * client's cases against every rule that's on. Shown on a company's Billing
 * tab and on an individual client's profile.
 */
import { FileText, RotateCw, Trash2, Upload } from "lucide-react";
import { useRef, useState, type FC } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { guidelineActions, ruleActions } from "../actions";
import { useGuidelines } from "../queries";
import type { GuidelineOwner, TimeGuideline } from "../types";
import { RuleList } from "./RuleList";
import { fileProblem, GUIDELINE_ACCEPT } from "./ruleModel";

const StatusTag: FC<{ guideline: TimeGuideline }> = ({ guideline }) => {
  if (guideline.status === "extracting")
    return (
      <Badge variant="secondary" className="gap-1.5 font-normal">
        <Spinner className="size-3" /> Reading the rules
      </Badge>
    );
  if (guideline.status === "failed")
    return (
      <Badge
        variant="secondary"
        className="border-destructive/30 bg-destructive/10 font-normal text-danger"
      >
        Couldn&apos;t Read
      </Badge>
    );
  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-normal",
        guideline.on_count && "border-primary/30 bg-primary/10 text-primary"
      )}
    >
      {guideline.on_count
        ? `${guideline.on_count} of ${guideline.rule_count} checked in pre-bill`
        : "No rules on"}
    </Badge>
  );
};

const GuidelineCard: FC<{
  guideline: TimeGuideline;
  canEdit: boolean;
  onRemove: () => void;
}> = ({ guideline, canEdit, onRemove }) => (
  <div
    className="flex flex-col gap-2.5 rounded-xl border border-line-2 p-3.5"
    data-testid="guideline-card"
  >
    <span className="flex items-center gap-2 text-base">
      <FileText className="size-3.5 shrink-0 text-fg-3" />
      <button
        type="button"
        className="truncate font-medium hover:underline"
        onClick={() => void guidelineActions.open(guideline)}
      >
        {guideline.filename}
      </button>
      <span className="flex-1" />
      <StatusTag guideline={guideline} />
      {canEdit && guideline.status !== "extracting" ? (
        <Button
          size="xs"
          variant="ghost"
          onClick={() => void guidelineActions.extract(guideline)}
        >
          <RotateCw /> Read again
        </Button>
      ) : null}
      {canEdit ? (
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={`Remove ${guideline.filename}`}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      ) : null}
    </span>
    {guideline.error ? (
      <span className="text-sm leading-4.5 text-warn">{guideline.error}</span>
    ) : null}
    {guideline.status === "extracting" ? (
      <Skeleton className="h-16" />
    ) : (
      <RuleList
        rules={guideline.rules}
        canEdit={canEdit}
        empty="No billing rules read from this file. Add them in plain English."
        onChange={(rules, message) =>
          void ruleActions.saveGuidelineRules(guideline, rules, message)
        }
      />
    )}
  </div>
);

type Props = {
  owner: GuidelineOwner;
  ownerName: string;
  canEdit: boolean;
};

export const GuidelinesSection: FC<Props> = ({ owner, ownerName, canEdit }) => {
  const { data, isLoading } = useGuidelines(owner, "guidelines");
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // The last one asked about stays set so the dialog's text holds while it fades out.
  const [removing, setRemoving] = useState<TimeGuideline | null>(null);
  const [confirming, setConfirming] = useState(false);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    const problem = fileProblem(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setUploading(true);
    await guidelineActions.upload(owner, file);
    setUploading(false);
  };
  const guidelines = data ?? [];
  return (
    <div className="flex flex-col gap-2.5" data-testid="guidelines-section">
      <div className="flex items-end gap-3">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium">
            Outside counsel guidelines
          </span>
          <span className="max-w-190 text-sm leading-4.5 text-fg-3">
            Rules read from {ownerName}&apos;s billing guidelines. Pre-bill
            checks released time on {ownerName}&apos;s cases against every rule
            that&apos;s on, and errors block approval until they&apos;re fixed.
          </span>
        </span>
        {canEdit ? (
          <>
            <input
              ref={input}
              type="file"
              accept={GUIDELINE_ACCEPT}
              className="hidden"
              aria-label="Guidelines file"
              data-testid="guideline-file-input"
              onChange={(e) => {
                void upload(e.currentTarget.files?.[0]);
                e.currentTarget.value = "";
              }}
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={uploading}
              onClick={() => input.current?.click()}
            >
              {uploading ? <Spinner /> : <Upload />} Upload guidelines
            </Button>
          </>
        ) : null}
      </div>
      {isLoading ? <Skeleton className="h-20" /> : null}
      {!isLoading && !guidelines.length ? (
        <span className="rounded-xl border border-dashed border-line-2 px-3.5 py-4 text-base text-fg-3">
          No guidelines on file. Upload {ownerName}&apos;s outside counsel
          guidelines (PDF, Word or text) and CaseBlink reads the billing rules
          out of them for you to review.
        </span>
      ) : null}
      {guidelines.map((g) => (
        <GuidelineCard
          key={g.id}
          guideline={g}
          canEdit={canEdit}
          onRemove={() => {
            setRemoving(g);
            setConfirming(true);
          }}
        />
      ))}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.filename}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its {removing?.rule_count ?? 0} rules stop applying to {ownerName}
              &apos;s cases. Flags already raised stay on their entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removing) void guidelineActions.remove(removing);
                setConfirming(false);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
